import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { CalendarClock, ChevronDown, CircleAlert, CirclePause } from 'lucide-react'
import { ActionPopover } from '@/components/action-popover'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { useActorName } from '@/hooks/use-actor-name'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useApproveMessage,
  useRejectMessage,
  useSendMessage,
  useUpdateMessage,
} from '@/features/communications/hooks'
import { POConversations } from '@/features/communications/po-conversations'
import { ActionEmailContext } from '@/features/communications/action-email'
import { ActionItemsList, CustomerActionCard } from '@/features/purchase-orders/actions'
import { ActionItemSourceProvider } from '@/features/purchase-orders/action-source'
import { ACTION_FOCUS_CLASS, actionTargetProps, useActionFocus, useMessageFocus } from '@/hooks/use-action-focus'
import { formatApiErrorMessage } from '@/lib/api-error'
import { cn } from '@/lib/utils'
import {
  useManufacturerDataAgent,
  useManufacturerDataChangeItems,
  useManufacturerDataThreads,
  useRevertManufacturerDataChange,
} from './hooks'
import {
  canRevert,
  changeRows,
  changeStatusChip,
  describeChange,
  describeItem,
  heldChangeAction,
  itemCountLabel,
  itemSubject,
  leadTimeCheckLine,
  shortDay,
  toPurchaseOrderAction,
  toPurchaseOrderReopens,
  type ChangeRow,
  type ChangeStatusChip,
  type LeadTimeCheckStatus,
  type ManufacturerDataAction,
  type ManufacturerDataChange,
  type ManufacturerDataChangeItem,
} from './model'

/** The sign-off on replies from the data@ mailbox: the company, never a staff name. */
export const DATA_SIGNOFF = 'Thank you,\nUSA Restaurant Suppliers\ndata@shopusars.com'

const CHANGES_SHOWN = 5

/** One line on when the next weekly check goes out and to whom, or why it won't. */
export function LeadTimeCheckHeader({
  status,
  onChange,
}: {
  status: LeadTimeCheckStatus
  /** Opens the switch (the preferred source's sheet). */
  onChange?: () => void
}) {
  const actorName = useActorName()
  const line = leadTimeCheckLine(status, actorName)
  const Icon = line.state === 'scheduled' ? CalendarClock : line.state === 'blocked' ? CircleAlert : CirclePause
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <Icon
        aria-hidden
        className={line.state === 'blocked' ? 'size-4 shrink-0 text-amber-600' : 'size-4 shrink-0 text-muted-foreground'}
      />
      {line.state === 'scheduled' ? (
        <p className="min-w-0 break-words">
          <span className="font-medium">Next lead-time check:</span> {line.when}
          {line.to.length > 0 && (
            <span className="text-muted-foreground"> · to {line.to.join(', ')}</span>
          )}
          {line.cc.length > 0 && (
            <span className="text-muted-foreground"> · cc {line.cc.join(', ')}</span>
          )}
        </p>
      ) : line.state === 'blocked' ? (
        <p className="min-w-0 break-words">
          <span className="font-medium">Next lead-time check won’t send:</span> {line.reason}
        </p>
      ) : (
        <p className="min-w-0 break-words">
          <span className="font-medium">Weekly lead-time check: Off</span>
          {line.reason && <span className="text-muted-foreground"> · {line.reason}</span>}
        </p>
      )}
      {onChange && (
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  )
}

const CHIP_TONES = {
  scheduled: 'progress',
  held: 'warning',
  reverted: 'neutral',
  discarded: 'neutral',
} as const satisfies Record<ChangeStatusChip['state'], string>

/** The chip on a change that is not simply applied; a held sheet's links to the item that decides it. */
function ChangeStatus({
  chip,
  manufacturerUuid,
  item,
}: {
  chip: ChangeStatusChip
  manufacturerUuid: string
  item: ManufacturerDataAction | null
}) {
  const pill = <StatusPill tone={CHIP_TONES[chip.state]}>{chip.label}</StatusPill>
  if (chip.state !== 'held' || !item) return pill
  return (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: manufacturerUuid }}
      search={{
        tab: 'emails',
        message: item.communication_message_id != null ? String(item.communication_message_id) : undefined,
        action: item.uuid,
      }}
      className="rounded-full hover:opacity-80 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
    >
      {pill}
    </Link>
  )
}

/** A photo opens full size in a new tab; a spec sheet by its link. */
function FileLink({ url, children, className }: { url: string; children: ReactNode; className?: string }) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- the file on the CDN, not a dashboard page
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'rounded-sm focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
        className,
      )}
    >
      {children}
    </a>
  )
}

/** What an item is about; a product's page when it names one. */
function ItemSubject({ item, className }: { item: ManufacturerDataChangeItem; className?: string }) {
  const subject = itemSubject(item)
  if (!item.product) return <span className={className}>{subject}</span>
  return (
    <Link
      to="/products/$productId"
      params={{ productId: item.product.uuid }}
      className={cn('hover:underline', className)}
    >
      {subject}
    </Link>
  )
}

/** One item's row: what it is about, then its before and after (or its file). */
function ChangeItemRow({ item }: { item: ManufacturerDataChangeItem }) {
  const subject = itemSubject(item)
  const url = item.new_value || null
  if (item.kind === 'product') {
    return (
      <li>
        <ItemSubject item={item} className="break-words" />
      </li>
    )
  }
  if (item.kind === 'image') {
    return (
      <li className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          {url && (
            <FileLink url={url} className="shrink-0">
              <img
                src={url}
                alt={`${subject} photo`}
                loading="lazy"
                className="size-8 rounded border bg-muted object-cover"
              />
            </FileLink>
          )}
          <ItemSubject item={item} className="min-w-0 break-words" />
        </span>
        <span className="min-w-0 truncate text-muted-foreground" title={describeItem(item)}>
          {describeItem(item)}
        </span>
      </li>
    )
  }
  if (item.kind === 'document') {
    return (
      <li className="flex items-baseline justify-between gap-3">
        <ItemSubject item={item} className="shrink-0" />
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="min-w-0 truncate text-muted-foreground" title={describeItem(item)}>
            {describeItem(item)}
          </span>
          {url && (
            <FileLink url={url} className="shrink-0 font-medium text-primary hover:underline">
              Spec sheet
            </FileLink>
          )}
        </span>
      </li>
    )
  }
  return (
    <li className="flex items-baseline justify-between gap-3">
      <ItemSubject item={item} className="min-w-0 break-words" />
      <span
        className={cn(
          // A moved warehouse names both addresses; let it wrap rather than push the row off a phone.
          item.kind === 'warehouse' ? 'min-w-0 break-words text-right' : 'shrink-0',
          'tabular-nums text-muted-foreground',
        )}
      >
        {describeItem(item)}
      </span>
    </li>
  )
}

/** Each product's, discount's, fact's, file's or warehouse's before and after, loaded when the row is opened. */
function ChangeItems({ changeId, label }: { changeId: number; label: string }) {
  const items = useManufacturerDataChangeItems(changeId, true)
  if (items.isLoading) return <Skeleton className="mt-2 h-16 w-full" />
  if (items.isError || !items.data) {
    return <p className="mt-2 text-xs text-muted-foreground">These couldn’t load. Close and reopen to try again.</p>
  }
  return (
    <ul aria-label={label} className="mt-2 max-h-72 space-y-1 overflow-y-auto pr-1 text-xs">
      {items.data.map((item) => (
        <ChangeItemRow key={item.id} item={item} />
      ))}
    </ul>
  )
}

function AgentChangeRow({
  row,
  manufacturerUuid,
  actions,
}: {
  row: ChangeRow
  manufacturerUuid: string
  actions: readonly ManufacturerDataAction[]
}) {
  const { change, scope, ids } = row
  const actorName = useActorName()
  const [open, setOpen] = useState(false)
  const revert = useRevertManufacturerDataChange(manufacturerUuid)
  const chip = changeStatusChip(change, actorName)
  const description = describeChange(change)
  const count = change.item_count > 0 ? itemCountLabel(change) : null
  const revertible = canRevert(change)

  const confirmRevert = (close: () => void) =>
    revert.mutate(ids, {
      onSuccess: (saved) =>
        toast.success(saved.length > 1 ? `${saved.length} changes reverted` : 'Change reverted'),
      onError: (error) => toast.error(formatApiErrorMessage('Not reverted', error)),
      onSettled: close,
    })

  return (
    <li className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 px-3 py-2 text-sm sm:grid-cols-[4.5rem_minmax(0,1fr)_auto]">
      <time dateTime={change.created_at} className="pt-px text-xs tabular-nums text-muted-foreground">
        {shortDay(change.created_at)}
      </time>
      <div className="min-w-0">
        <p className="break-words font-medium">
          {description}
          {scope && <span className="font-normal text-muted-foreground"> · {scope}</span>}
          {chip && (
            <>
              {' '}
              <ChangeStatus chip={chip} manufacturerUuid={manufacturerUuid} item={heldChangeAction(change, actions)} />
            </>
          )}
        </p>
        {change.quote && (
          <p className="mt-0.5 line-clamp-2 break-words text-[13px] text-foreground/75" title={change.quote}>
            “{change.quote}”
          </p>
        )}
        {(count || revertible) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {count && (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                className="inline-flex items-center gap-1 rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                {open ? `Hide ${count.replace(/^\d+ /, '')}` : `Show ${count}`}
                <ChevronDown aria-hidden className={cn('size-3 transition-transform', open && 'rotate-180')} />
              </button>
            )}
            {revertible && (
              <ActionPopover
                trigger={
                  <button
                    type="button"
                    className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                  >
                    Revert
                  </button>
                }
                title="Revert this change?"
                confirmLabel="Revert"
                pending={revert.isPending}
                pendingLabel="Reverting…"
                onConfirm={(_note, close) => confirmRevert(close)}
              >
                <p className="text-xs text-muted-foreground">
                  {description}
                  {scope ? ` · ${scope}` : ''}. Puts back what it replaced; anything changed since stays as it is.
                </p>
              </ActionPopover>
            )}
          </div>
        )}
        {open && <ChangeItems changeId={change.id} label={`${description} items`} />}
      </div>
      {change.communication_message_id != null && (
        <Link
          to="/manufacturers/$manuId"
          params={{ manuId: manufacturerUuid }}
          search={{ tab: 'emails', message: String(change.communication_message_id) }}
          className="col-start-2 text-xs font-medium text-primary hover:underline sm:col-start-3 sm:row-start-1 sm:pt-px"
        >
          Email →
        </Link>
      )}
    </li>
  )
}

/** What the agent changed, newest first: the change, the manufacturer's own words, and the email. */
export function AgentChanges({
  manufacturerUuid,
  changes,
  actions = [],
}: {
  manufacturerUuid: string
  changes: readonly ManufacturerDataChange[]
  /** The agent's items, for the one a held price sheet waits on. */
  actions?: readonly ManufacturerDataAction[]
}) {
  const [showAll, setShowAll] = useState(false)
  if (changes.length === 0) return null
  const rows = changeRows(changes)
  const shown = showAll ? rows : rows.slice(0, CHANGES_SHOWN)
  return (
    <section aria-labelledby="mfr-data-changes" className="space-y-2">
      <h3
        id="mfr-data-changes"
        className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        What the agent changed
      </h3>
      <ul className="divide-y rounded-lg border bg-card">
        {shown.map((row) => (
          <AgentChangeRow key={row.key} row={row} manufacturerUuid={manufacturerUuid} actions={actions} />
        ))}
      </ul>
      {rows.length > CHANGES_SHOWN && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </Button>
      )}
    </section>
  )
}

/**
 * The manufacturer page's Emails tab (USA-1474): the weekly lead-time check, the data@ conversations on
 * the PO thread components with each item's PO action card under its email, and what the agent changed.
 */
export function ManufacturerEmailsTab({
  manufacturerUuid,
  messageId,
  threadUuid,
  actionUuid,
  onChangeCheck,
}: {
  manufacturerUuid: string
  /** The email a link opened: its conversation is shown and the email brought into view. */
  messageId?: number
  threadUuid?: string
  /** The item opened from the Action Items page: brought into view and highlighted. */
  actionUuid?: string
  onChangeCheck?: () => void
}) {
  const agent = useManufacturerDataAgent(manufacturerUuid)
  const threads = useManufacturerDataThreads(manufacturerUuid)
  useActionFocus(actionUuid, !!agent.data && !!threads.data)
  useMessageFocus(messageId, !!threads.data)
  const approve = useApproveMessage()
  const reject = useRejectMessage()
  const send = useSendMessage()
  const update = useUpdateMessage()
  const failed = (title: string) => (error: unknown) => toast.error(formatApiErrorMessage(title, error))
  const [showResolvedActions, setShowResolvedActions] = useState(false)

  if (agent.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-5 w-96 max-w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (!agent.data) {
    return <p className="text-sm text-muted-foreground">Emails could not be loaded. Try again shortly.</p>
  }

  const data = agent.data
  const actions = data.actions.map(toPurchaseOrderAction)
  const reopens = toPurchaseOrderReopens(data.action_reopens)
  const open = actions.filter((action) => action.status === 'open')
  // Items an email raised are worked under that email; the rest, and every closed one, are listed here.
  const looseOpen = open.filter((action) => action.communication_message_id == null)
  const closed = actions.filter((action) => action.status !== 'open')

  return (
    <ActionItemSourceProvider value={{ kind: 'manufacturer_data', manufacturerUuid }}>
      <div className="space-y-6">
        <LeadTimeCheckHeader status={data.lead_time_check} onChange={onChangeCheck} />

        {looseOpen.length + closed.length > 0 && (
          <section className="space-y-3" aria-labelledby="mfr-data-items">
            <h3
              id="mfr-data-items"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Actions
            </h3>
            <ActionItemsList
              poId=""
              openActions={looseOpen}
              resolvedActions={closed}
              reopens={reopens}
              shares={[]}
              lineItems={[]}
              showResolvedActions={showResolvedActions}
              setShowResolvedActions={setShowResolvedActions}
              showEmptyState={false}
            />
          </section>
        )}

        <ActionEmailContext.Provider
          value={{
            focusMessageId: messageId,
            renderAction: (item) => {
              const action = open.find((candidate) => candidate.id === item.id)
              return action ? (
                <div {...actionTargetProps(action.uuid)} className={`rounded-xl ${ACTION_FOCUS_CLASS}`}>
                  <CustomerActionCard action={action} reopens={reopens} />
                </div>
              ) : null
            },
          }}
        >
          <POConversations
            title="Emails"
            initialThreadUuid={threadUuid}
            threads={threads.data}
            isLoading={threads.isLoading}
            actions={actions}
            hasPoPdf={false}
            hasQuotePdf={false}
            shipmentDocOptions={[]}
            contextForThread={() => ({
              signoff: DATA_SIGNOFF,
              poDocuments: false,
              canRecord: false,
            })}
            onApprove={(uuid) => approve.mutate(uuid, { onError: failed('Not sent') })}
            onReject={(uuid) => reject.mutate(uuid, { onError: failed('Not rejected') })}
            onSend={(uuid) => send.mutate(uuid, { onError: failed('Not sent') })}
            onUpdate={(uuid, body) =>
              update.mutate({ messageUuid: uuid, body }, { onError: failed('Draft not saved') })
            }
            newThreadAction={null}
            emptyState={
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                No emails with this manufacturer yet
              </div>
            }
          />
        </ActionEmailContext.Provider>

        <AgentChanges manufacturerUuid={manufacturerUuid} changes={data.changes} actions={data.actions} />
      </div>
    </ActionItemSourceProvider>
  )
}
